import PlanCard from '@/domain/models/PlanCard';
import Subscription from '@/domain/models/Subscription';
import {
  RawPlanCard, RawPlanCardTimeOut,
  RawSubscriptions,
  RawWrapper,
  RawWrapperTimeOut,
  TimeOutContent,
} from '@/domain/services/types';
import PlanCardElement from '@/domain/models/PlanCardElement';

/* eslint camelcase: ["error", {allow: [".*"]}] */
type Sortable = {
  fare_value: number,
};

/* eslint camelcase: ["error", {allow: [".*"]}] */
type SortableByOriginalPrice = {
  fare_original_price: number,
};

function sortByPriceLowerToHigher(obj: Array<RawPlanCardTimeOut>) {
  return obj.sort((a: Sortable, b: Sortable) => {
    if (a.fare_value > b.fare_value) {
      return 1;
    }
    return -1;
  });
}

function sortSubscriptionsByPriceLowerToHigher(obj: Array<RawSubscriptions>) {
  return obj.sort((a: SortableByOriginalPrice, b: SortableByOriginalPrice) => {
    if (a.fare_original_price > b.fare_original_price) {
      return -1;
    }
    return 1;
  });
}

function buildElements(elemnts: Array<string>): Array<PlanCardElement> {
  return elemnts.map((element: string) => {
    const splitElements = element.split(',');
    return new PlanCardElement(splitElements[0], splitElements[1]);
  });
}

function buildSubscription(
  pack: RawSubscriptions,
  option: RawPlanCardTimeOut,
): Subscription {
  return new Subscription(
    pack.fare_id,
    pack.fare_name,
    pack.plan_type,
    pack.fare_value,
    pack.fare_price_per_month,
    pack.fare_original_price,
    pack.coupon_duration,
    pack.coupon_amount_discount,
    pack.fare_payment_conditions ?? '',
    pack.fare_conditions ?? pack.conditions ?? option.fare_conditions ?? '',
    pack.promo,
    pack.stripe_product_id,
    buildElements(pack.fare_includes_landing),
    option.fare_highlighted_features,
    option.fare_not_includes,
    option.fare_includes,
    pack.active_plan,
    pack.coupon_type,
    pack.fare_price_per_month_coupon,
    pack.new_promo,
    pack.period,
    pack.coupon_new_promo,
  );
}

class PlanCardFactory {
  getPlans(data: RawWrapperTimeOut): Array<PlanCard> {
    const screenTitle = data.components.title ?? '';
    const subtitle = data.components.subtitle ?? '';

    return sortByPriceLowerToHigher(data.components.options)
      .map((option: RawPlanCardTimeOut) => {
        const subscriptions = sortSubscriptionsByPriceLowerToHigher(option.packs)
          .map((pack: RawSubscriptions) => buildSubscription(pack, option));

        return new PlanCard(
          option.fare_id,
          option.fare_name,
          subtitle || option.fare_text,
          subscriptions,
          screenTitle,
        );
      });
  }

  buildTimeOutContent(plans: PlanCard[]): TimeOutContent | null {
    const plan = plans[0];
    const subscription = plan?.subscriptions?.[0];

    if (!plan || !subscription) {
      return null;
    }

    return {
      planType: subscription.planType,
      planLabel: subscription.newPromo ?? '',
      price: subscription.priceMonth,
      subtitle: plan.description,
      screenTitle: plan.screenTitle,
      conditions: subscription.conditions,
      elementsIncluded: subscription.elementsIncludedFront ?? [],
      elementsNotIncluded: subscription.elementsNotIncludedFront ?? [],
      elementsFeatured: subscription.elementsFeatured ?? [],
      paymentProductId: subscription.paymentProductId,
    };
  }

  getAmaproPlan(data: RawWrapperTimeOut): Array<PlanCard> {
    return data.components.options
      .map((option: RawPlanCardTimeOut) => {
        const subscriptions = sortSubscriptionsByPriceLowerToHigher(option.packs)
          .map((pack: RawSubscriptions) => buildSubscription(pack, option));
        return new PlanCard(option.fare_id, option.fare_name, option.fare_text, subscriptions);
      });
  }
}

export default new PlanCardFactory();
